import React from 'react';
import clsx from 'clsx';

import {composeClasses, styled, WithClasses} from '@netx/core-components/Theme';
import {DragResizeDirection, DRAG_RESIZE_DEFAULT_DIRECTION} from './constants';
import {
	dragResizeHandleClasses,
	getDragResizeHandleUtilityClass,
	DragResizeHandleClasses,
	DRAG_RESIZE_HANDLE_CLASS_NAME,
} from './dragResizeHandleClasses';

type DragResizeHandleUtiltiyClassProps = WithClasses<
	DragResizeHandleClasses,
	{
		dragging?: boolean;
		direction: DragResizeDirection;
	}
>;

const useUtilityClasses = (ownerState: DragResizeHandleUtiltiyClassProps) => {
	const {classes, direction, dragging} = ownerState;
	const slots = {
		root: ['root', direction, dragging && 'dragging'],
		ellipsis: ['ellipsis'],
	};
	return composeClasses(slots, getDragResizeHandleUtilityClass, classes);
};

const DragResizeHandleRoot = styled('div', {
	name: DRAG_RESIZE_HANDLE_CLASS_NAME,
	slot: 'Root',
})(({theme, ownerState}) => {
	const {direction} = ownerState as DragResizeHandleUtiltiyClassProps;
	return {
		alignItems: 'center',
		background: theme.palette.custom.background.accent,
		display: 'flex',
		justifyContent: 'center',
		// opacity: 1,
		position: 'absolute',
		// transition: theme.transitions.create('opacity', {
		// 	duration: theme.transitions.duration.short,
		// 	easing: theme.transitions.easing.easeInOut,
		// }),
		userSelect: 'none !important',
		zIndex: 10, //theme.zIndex.drawer,
		...([DragResizeDirection.LEFT, DragResizeDirection.RIGHT].includes(
			direction,
		)
			? {
					bottom: 0,
					cursor: 'col-resize',
					top: 0,
					width: 3,
					...(direction === DragResizeDirection.LEFT
						? {
								borderLeft: '1px solid transparent',
								left: 0,
						  }
						: {
								borderRight: '1px solid transparent',
								right: 0,
						  }),
					[`& .${dragResizeHandleClasses.ellipsis}`]: {
						height: 17,
						marginLeft: 8,
						transform: 'rotate(90deg)',
					},
			  }
			: {
					cursor: 'row-resize',
					height: 3,
					left: 0,
					right: 0,
					...(direction === DragResizeDirection.TOP
						? {
								borderTop: '1px solid transparent',
								top: 0,
						  }
						: {
								borderBottom: '1px solid transparent',
								bottom: -1,
						  }),
					[`& .${dragResizeHandleClasses.ellipsis}`]: {
						width: 17,
					},
			  }),
		[`&:hover, &.${dragResizeHandleClasses.dragging}`]: {
			borderColor: theme.palette.secondary.main,
		},
	} as any;
});

const DragResizeHandleEllipsis = styled('div', {
	name: DRAG_RESIZE_HANDLE_CLASS_NAME,
	slot: 'ellipsis',
})(({theme}) => ({
	display: 'none',
	color:
		theme.palette.mode === 'light'
			? 'rgba(0, 0, 0, 0.2)'
			: 'rgba(255, 255, 255, 0.2)',
	fontSize: '20px',
	userSelect: 'none',
}));

export type DragResizeHandleProps = WithClasses<
	DragResizeHandleClasses,
	React.DetailedHTMLProps<
		React.HTMLAttributes<HTMLDivElement>,
		HTMLDivElement
	> & {
		direction?: DragResizeDirection;
		isDragging?: boolean;
	}
>;

export const DragResizeHandle = ({
	classes: _classes,
	className,
	direction = DRAG_RESIZE_DEFAULT_DIRECTION,
	isDragging,
	...props
}: DragResizeHandleProps) => {
	const ownerState = {
		classes: _classes,
		direction,
		dragging: isDragging,
	};

	const classes = useUtilityClasses(ownerState);

	return (
		<DragResizeHandleRoot
			role="none"
			{...props}
			className={clsx(classes.root, className)}
			ownerState={ownerState}
		>
			<DragResizeHandleEllipsis className={classes.ellipsis}>
				&#8230;
			</DragResizeHandleEllipsis>
		</DragResizeHandleRoot>
	);
};

export default DragResizeHandle;
